import { Flex, List, Text } from "@wayflyer/flyui";
import type { ReactNode } from "react";
import { defineMessages, useIntl, type MessageDescriptor } from "react-intl";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import type { AccountingDocumentsValidationResult } from "../../../hooks/useAccountingDocumentsValidation";
import { UploadDocumentType } from "../../../types";

// global:
const NOT_ENOUGH_DATA = "not-enough-data";
const STALE_DATA = "stale-data";
const GAPS_IN_DATA = "gaps-in-data";

// by file:
const TYPE_MISMATCH = "TYPE_MISMATCH";
const EXTRANEOUS_INFO = "EXTRANEOUS_INFO";
const NO_MONTHLY_PERIODS = "NO_MONTHLY_PERIODS";
const PERIOD_GAPS = "PERIOD_GAPS";
const MULTIPLE_CURRENCIES = "MULTIPLE_CURRENCIES";
const MULTIPLE_ENTITIES = "MULTIPLE_ENTITIES";

const messages = defineMessages<string>({
  [NOT_ENOUGH_DATA]: {
    id: "funding-application: document validation error not-enough-data",
    defaultMessage: "A minimum of {requiredDuration} months of accounting data must be included.",
  },
  [STALE_DATA]: {
    id: "funding-application: document validation error stale-data",
    defaultMessage: "{documentType} must include financial data from within the last three months.",
  },
  [GAPS_IN_DATA]: {
    id: "funding-application: document validation error gaps-in-data",
    defaultMessage: "Your {documentType} appears to have gaps in the reported time periods.",
  },
  [TYPE_MISMATCH]: {
    id: "funding-application: document validation error TYPE_MISMATCH",
    defaultMessage: "This file does not appear to contain a {documentType}.",
  },
  [EXTRANEOUS_INFO]: {
    id: "funding-application: document validation error EXTRANEOUS_INFO",
    defaultMessage: "This document contains parts that do not pertain to a {documentType}.",
  },
  [NO_MONTHLY_PERIODS]: {
    id: "funding-application: document validation error NO_MONTHLY_PERIODS",
    defaultMessage: "No monthly periods were detected.",
  },
  [PERIOD_GAPS]: {
    id: "funding-application: document validation error PERIOD_GAPS",
    defaultMessage: "The document appears to have gaps in the reported time periods.",
  },
  [MULTIPLE_CURRENCIES]: {
    id: "funding-application: document validation error MULTIPLE_CURRENCIES",
    defaultMessage: "Detected multiple currencies within the document: {currencies}.",
  },
  [MULTIPLE_ENTITIES]: {
    id: "funding-application: document validation error MULTIPLE_ENTITIES",
    defaultMessage: "Detected multiple entities within the document, suggesting it might be unconsolidated.",
  },
});

const DOC_TYPE_TO_NAME: Record<string, MessageDescriptor> = {
  [UploadDocumentType.PROFIT_AND_LOSS]: COMMON_MESSAGES.documentUploadProfitAndLoss,
  [UploadDocumentType.BALANCE_SHEET]: COMMON_MESSAGES.documentUploadBalanceSheet,
};

const REQUIRED_DURATION = 24;

export const useDocumentValidationMessages = (
  { issuesByFileId, globalIssues, files }: AccountingDocumentsValidationResult,
  documentType: UploadDocumentType
): ReactNode => {
  const intl = useIntl();
  return (
    <Flex direction="column" gap="4" padding={["4", "0", "0", "0"]}>
      {Boolean(globalIssues.length) && (
        <List>
          {globalIssues.map((issueCode) => (
            <List.Item key={issueCode}>
              {intl.formatMessage(messages[issueCode], {
                documentType: intl.formatMessage(DOC_TYPE_TO_NAME[documentType]),
                requiredDuration: REQUIRED_DURATION,
              })}
            </List.Item>
          ))}
        </List>
      )}

      {Object.keys(issuesByFileId).map((id) => (
        <Flex direction="column" key={id} gap="1">
          <Text fontWeight="semiBold">{files.find((file) => file.id === id)?.name}:</Text>
          <List>
            {issuesByFileId[id].map(({ code }) => (
              <List.Item key={code}>
                {intl.formatMessage(messages[code], {
                  documentType: intl.formatMessage(DOC_TYPE_TO_NAME[documentType]),
                  requiredDuration: REQUIRED_DURATION,
                  currencies: files.find((file) => file.id === id)?.validatorExtractedCurrencies.join(", "),
                })}
              </List.Item>
            ))}
          </List>
        </Flex>
      ))}
    </Flex>
  );
};
